import Phaser from 'phaser';
import { GAME_CONFIG, PLAYER_CONFIG } from '../../config/game';
import { BlanketBoss } from '../../entities/BlanketBoss';
import { BaseBossScene, BossSceneConfig } from './BaseBossScene';

/**
 * BlanketBossScene (Level 1-1)
 * 
 * Theme: Emily hiding under a blanket fortress
 * Uses Sobel edge-based physics for platforms
 * Extends BaseBossScene for shared functionality
 */
export class BlanketBossScene extends BaseBossScene {
  private boss!: BlanketBoss;
  private specialMeter!: Phaser.GameObjects.Graphics;
  private specialValue: number = 0;
  private attackHintText?: Phaser.GameObjects.Text;

  constructor() {
    const config: BossSceneConfig = {
      levelId: '1-1',
      levelName: 'The Blanket Fortress',
      backgroundKey: '1-1_blanket_fortress',
      bossName: 'EMILY & THE BLANKET FORTRESS',
      useEdgePhysics: true,
    };
    super('BlanketBossScene', config);
  }

  create() {
    // Load edge data before base create
    this.loadEdgeData();
    
    // Call base create (sets up player, platforms, projectiles, UI, controls)
    super.create();
    
    const { width, height } = GAME_CONFIG;
    
    // Create edge overlay
    this.createEdgeOverlay(width, height);
    
    // Ensure player renders in front
    this.player.setDepth(100);
    
    // Setup attack key for X/Z
    if (this.input.keyboard) {
      this.input.keyboard.addKey('Z').on('down', () => {
        if (!this.isPaused && !this.isGameOver) this.handlePlayerAttack();
      });
    }
    
    // Create special meter UI
    this.createSpecialMeterUI(width);
  }

  protected createBackground(width: number, height: number) {
    // Try to use the generated side-view background
    if (this.textures.exists('1-1_blanket_fortress')) {
      const bgImage = this.add.image(width / 2, height / 2, '1-1_blanket_fortress');
      bgImage.setDisplaySize(width, height);
      bgImage.setDepth(0);
    } else if (this.textures.exists('emily_bedroom_side')) {
      const bgImage = this.add.image(width / 2, height / 2, 'emily_bedroom_side');
      bgImage.setDisplaySize(width, height);
      bgImage.setDepth(0);
    } else {
      // Fallback: Programmatic background - just walls
      const bg = this.add.graphics();
      
      // Wall gradient (soft pink/purple for Emily's room)
      bg.fillGradientStyle(0x4a2040, 0x4a2040, 0x2d1b4e, 0x2d1b4e, 1);
      bg.fillRect(0, 0, width, height);
      bg.setDepth(0);

      // Window (decorative) with curtains
      bg.fillStyle(0x1a1a2e, 1);
      bg.fillRect(width - 100, 80, 70, 100);
      bg.lineStyle(4, 0x8b5a2b, 1);
      bg.strokeRect(width - 100, 80, 70, 100);
      bg.lineBetween(width - 65, 80, width - 65, 180);
      bg.lineBetween(width - 100, 130, width - 30, 130);
      
      // Pink curtains
      bg.fillStyle(0xff69b4, 0.6);
      bg.fillRect(width - 110, 70, 15, 120);
      bg.fillRect(width - 25, 70, 15, 120);

      // Stars in window (nighttime)
      for (let i = 0; i < 8; i++) {
        const star = this.add.graphics();
        star.fillStyle(0xffff00, Phaser.Math.FloatBetween(0.5, 1));
        star.fillCircle(
          Phaser.Math.Between(width - 95, width - 35),
          Phaser.Math.Between(85, 125),
          Phaser.Math.Between(1, 3)
        );
        star.setDepth(1);
      }
    }

    // Floor - wooden (always draw on top of background)
    const floor = this.add.graphics();
    floor.fillStyle(0x5d4037, 1);
    floor.fillRect(0, height - 40, width, 40);
    floor.fillStyle(0x4a3429, 0.5);
    for (let i = 0; i < width; i += 40) {
      floor.fillRect(i, height - 40, 2, 40);
    }
    floor.setDepth(2);

    // Wall decorations - stars/hearts (subtle)
    for (let i = 0; i < 8; i++) {
      const decor = this.add.text(
        Phaser.Math.Between(20, width - 40),
        Phaser.Math.Between(80, 350),
        Phaser.Math.Between(0, 1) ? '⭐' : '💖',
        { fontSize: '12px' }
      );
      decor.setAlpha(0.2);
      decor.setDepth(1);
    }
  }

  protected createPlatforms(width: number, height: number) {
    this.platforms = this.physics.add.staticGroup();
    
    // Only ground platform - everything else uses Sobel edge physics!
    const ground = this.add.rectangle(width / 2, height - 40, width, 80, 0x5d4037);
    ground.setAlpha(0); // Invisible since we draw it in background
    this.physics.add.existing(ground, true);
    this.platforms.add(ground);
  }

  protected createBoss(width: number, _height: number) {
    // Create boss - on a strong edge area
    this.boss = new BlanketBoss(this, width * 0.6, 624 - 50, this.projectiles);
    
    // Boss collision with platforms
    this.physics.add.collider(this.boss, this.platforms);
  }

  private createSpecialMeterUI(_width: number) {
    // Special meter (parry bonus)
    this.specialMeter = this.add.graphics();
    this.specialMeter.setDepth(100);
    this.updateSpecialMeter();
  }

  private updateSpecialMeter() {
    const { width } = GAME_CONFIG;
    this.specialMeter.clear();

    // Background
    this.specialMeter.fillStyle(0x333333, 1);
    this.specialMeter.fillRoundedRect(width - 70, 20, 50, 100, 4);

    // Fill
    const fillHeight = 96 * this.specialValue;
    this.specialMeter.fillStyle(0xffd700, 1);
    this.specialMeter.fillRoundedRect(width - 68, 22 + (96 - fillHeight), 46, fillHeight, 2);
  }

  protected handlePlayerAttack() {
    if (this.isPaused || this.isGameOver) return;

    // Check if player is close enough to boss
    const distance = Phaser.Math.Distance.Between(
      this.player.x, this.player.y,
      this.boss.x, this.boss.y
    );

    if (distance < 100 && this.boss.isVulnerable()) {
      // Hit the boss!
      this.boss.takeDamage(15);
      this.showHitEffect();
      this.cameras.main.shake(100, 0.01);
    } else if (distance < 100) {
      // Boss not vulnerable - show hint
      this.showAttackHint('Wait for Emily to peek out!');
    } else {
      // Too far away
      this.showAttackHint('Get closer!');
    }
  }

  private showHitEffect() {
    if (!this.isSceneActive()) return;
    
    const { width, height } = GAME_CONFIG;
    
    // Flash effect
    const flash = this.add.rectangle(width / 2, height / 2, width, height, 0xffffff, 0.3);
    this.tweens.add({
      targets: flash,
      alpha: 0,
      duration: 100,
      onComplete: () => {
        if (!this.isSceneActive()) return;
        if (flash && flash.scene) flash.destroy();
      },
    });

    // Damage number
    const dmgText = this.add.text(this.boss.x + Phaser.Math.Between(-20, 20), this.boss.y - 50, '-15', {
      fontFamily: 'Georgia, serif',
      fontSize: '18px',
      color: '#ff4444',
      stroke: '#000000',
      strokeThickness: 3,
    }).setOrigin(0.5).setDepth(200);
    
    this.tweens.add({
      targets: dmgText,
      y: this.boss.y - 90,
      alpha: 0,
      scale: 1.3,
      duration: 600,
      ease: 'Back.easeOut',
      onComplete: () => {
        if (!this.isSceneActive()) return;
        if (dmgText && dmgText.scene) dmgText.destroy();
      },
    });

    // Hit text
    const hitText = this.add.text(this.boss.x, this.boss.y - 60, 'HIT!', {
      fontFamily: 'Georgia, serif',
      fontSize: '24px',
      color: '#ffd700',
      stroke: '#000000',
      strokeThickness: 4,
    });
    hitText.setOrigin(0.5, 0.5);

    this.tweens.add({
      targets: hitText,
      y: this.boss.y - 100,
      alpha: 0,
      duration: 500,
      onComplete: () => {
        if (!this.isSceneActive()) return;
        if (hitText && hitText.scene) hitText.destroy();
      },
    });
  }

  private showAttackHint(message: string) {
    if (!this.isSceneActive()) return;
    
    if (this.attackHintText) {
      this.attackHintText.destroy();
    }

    this.attackHintText = this.add.text(this.player.x, this.player.y - 50, message, {
      fontFamily: 'Georgia, serif',
      fontSize: '12px',
      color: '#ffcc00',
      backgroundColor: '#000000aa',
      padding: { x: 6, y: 4 },
    });
    this.attackHintText.setOrigin(0.5, 0.5);

    this.tweens.add({
      targets: this.attackHintText,
      alpha: 0,
      delay: 1000,
      duration: 300,
      onComplete: () => {
        if (!this.isSceneActive()) return;
        if (this.attackHintText && this.attackHintText.scene) {
          this.attackHintText.destroy();
          this.attackHintText = undefined;
        }
      },
    });
  }

  protected onParry() {
    if (!this.isSceneActive()) return;
    
    // Parry builds special meter
    this.specialValue = Math.min(1, this.specialValue + 0.25);
    this.updateSpecialMeter();
    this.showParryEffect(this.player.x, this.player.y);
  }

  private showParryEffect(x: number, y: number) {
    if (!this.isSceneActive()) return;
    
    // Pink flash
    const flash = this.add.graphics();
    flash.fillStyle(0xff69b4, 1);
    flash.fillCircle(x, y, 30);

    this.tweens.add({
      targets: flash,
      alpha: 0,
      scaleX: 2,
      scaleY: 2,
      duration: 300,
      onComplete: () => {
        if (!this.isSceneActive()) return;
        if (flash && flash.scene) flash.destroy();
      },
    });

    // "PARRY!" text
    const parryText = this.add.text(x, y - 20, 'PARRY!', {
      fontFamily: 'Georgia, serif',
      fontSize: '18px',
      color: '#ff69b4',
      fontStyle: 'bold',
    });
    parryText.setOrigin(0.5, 0.5);

    this.tweens.add({
      targets: parryText,
      y: y - 50,
      alpha: 0,
      duration: 500,
      onComplete: () => {
        if (!this.isSceneActive()) return;
        if (parryText && parryText.scene) parryText.destroy();
      },
    });
  }

  // Override to handle parryable projectiles with special meter
  protected handleProjectileHit(
    player: Phaser.GameObjects.GameObject,
    projectile: Phaser.GameObjects.GameObject
  ) {
    if (this.isGameOver || this.isPaused || !this.isSceneActive()) return;

    const proj = projectile as Phaser.Physics.Arcade.Image;
    const playerObj = player as any;

    // Safety check - make sure projectile is still active
    if (!proj || !proj.active) return;

    // Check if pink (parryable) and player is parrying
    if (proj.getData('parryable') && playerObj.isParrying && playerObj.isParrying()) {
      // Successful parry!
      proj.destroy();
      this.onParry();
      return;
    }

    // Skip if player is invincible
    if (playerObj.isInvincible && playerObj.isInvincible()) return;

    // Player takes damage
    proj.destroy();
    const stillAlive = playerObj.takeDamage();
    this.updateHearts();

    if (!stillAlive) {
      this.onDefeat();
    }
  }

  protected showBossIntro(width: number, height: number) {
    this.isPaused = true;

    // Custom intro for blanket fortress
    const panel = this.add.graphics();
    panel.fillStyle(0x000000, 0.85);
    panel.fillRect(0, height * 0.25, width, height * 0.5);

    const levelText = this.add.text(width / 2, height * 0.30, 'WORLD 1-1', {
      fontFamily: 'Georgia, serif',
      fontSize: '16px',
      color: '#a78bfa',
    });
    levelText.setOrigin(0.5, 0.5);

    const bossName = this.add.text(width / 2, height * 0.38, 'THE BLANKET FORTRESS', {
      fontFamily: 'Georgia, serif',
      fontSize: '26px',
      color: '#ffd700',
      stroke: '#4a3800',
      strokeThickness: 4,
    });
    bossName.setOrigin(0.5, 0.5);
    bossName.setScale(0);

    const tagline = this.add.text(width / 2, height * 0.45, '"She will not emerge."', {
      fontFamily: 'Georgia, serif',
      fontSize: '14px',
      color: '#e0e0e0',
      fontStyle: 'italic',
    });
    tagline.setOrigin(0.5, 0.5);
    tagline.setAlpha(0);

    const mission = this.add.text(width / 2, height * 0.53, '🎯 MISSION: Coax Emily out of her blanket!', {
      fontFamily: 'Georgia, serif',
      fontSize: '13px',
      color: '#22c55e',
    });
    mission.setOrigin(0.5, 0.5);
    mission.setAlpha(0);

    const controlsTitle = this.add.text(width / 2, height * 0.60, '— CONTROLS —', {
      fontFamily: 'Georgia, serif',
      fontSize: '11px',
      color: '#888888',
    });
    controlsTitle.setOrigin(0.5, 0.5);
    controlsTitle.setAlpha(0);

    const controls = this.add.text(width / 2, height * 0.68, 
      '⬅️ ➡️  or  A/D    Move\n' +
      '⬆️  or  W/SPACE   Jump\n' +
      'SHIFT             Dash (dodge!)\n' +
      'X or Z            ATTACK when Emily peeks!\n' +
      '💗 Pink objects = Parry by jumping into them!', {
      fontFamily: 'monospace',
      fontSize: '10px',
      color: '#ffffff',
      align: 'left',
      lineSpacing: 5,
    });
    controls.setOrigin(0.5, 0.5);
    controls.setAlpha(0);

    const ready = this.add.text(width / 2, height * 0.80, '— GET READY! —', {
      fontFamily: 'Georgia, serif',
      fontSize: '14px',
      color: '#ffd700',
    });
    ready.setOrigin(0.5, 0.5);
    ready.setAlpha(0);

    // Animations
    this.tweens.add({
      targets: bossName,
      scaleX: 1,
      scaleY: 1,
      duration: 500,
      ease: 'Back.easeOut',
    });

    this.tweens.add({
      targets: tagline,
      alpha: 1,
      delay: 400,
      duration: 400,
    });

    this.tweens.add({
      targets: [mission, controlsTitle, controls],
      alpha: 1,
      delay: 800,
      duration: 500,
    });

    this.tweens.add({
      targets: ready,
      alpha: { from: 0, to: 1 },
      delay: 1500,
      duration: 300,
      yoyo: true,
      repeat: 2,
    });

    // Remove intro after delay
    this.safeDelayedCall(3500, () => {
      this.tweens.add({
        targets: [panel, levelText, bossName, tagline, mission, controlsTitle, controls, ready],
        alpha: 0,
        duration: 300,
        onComplete: () => {
          if (!this.isSceneActive()) return;
          
          [panel, levelText, bossName, tagline, mission, controlsTitle, controls, ready].forEach(obj => {
            if (obj && obj.scene) obj.destroy();
          });
          
          // Guard against accessing boss if scene is invalid
          if (!this.isSceneActive() || !this.boss || !this.boss.active) return;
          
          this.isPaused = false;
          if (this.boss && this.boss.active) {
            this.boss.startBattle();
          }
          this.showControlReminder();
        },
      });
    });
  }

  private showControlReminder() {
    if (!this.isSceneActive()) return;
    
    const { width, height } = GAME_CONFIG;
    
    const reminder = this.add.text(width / 2, height - 20, 
      '←→ Move | SPACE Jump | SHIFT Dash | X Attack', {
      fontFamily: 'monospace',
      fontSize: '10px',
      color: '#666666',
      backgroundColor: '#00000088',
      padding: { x: 10, y: 5 },
    });
    reminder.setOrigin(0.5, 0.5);

    this.tweens.add({
      targets: reminder,
      alpha: 0,
      delay: 8000,
      duration: 2000,
      onComplete: () => {
        if (!this.isSceneActive()) return;
        if (reminder && reminder.scene) reminder.destroy();
      },
    });
  }

  protected updateBoss(_time: number, _delta: number) {
    if (this.isGameOver || !this.isSceneActive()) return;
    
    // Guard against accessing boss if it doesn't exist
    if (!this.boss || !this.boss.active) return;
    
    // Update boss
    this.boss.update(_time, _delta);

    // Update boss health display
    if (this.boss && this.boss.active) {
      this.updateBossHealthBar(this.boss.getHealthPercent());

      // Check boss defeated (only once)
      if (this.boss.isDefeated() && !this.isGameOver) {
        this.onVictory();
      }
    }
  }

  // Override update to handle unique controls (WASD, edge physics boost)
  update(time: number, delta: number) {
    if (this.isPaused || this.isGameOver || !this.isSceneActive()) return;
    
    // Guard against accessing player if it doesn't exist
    if (!this.player || !this.player.active) return;

    let velocityX = 0;

    // Mobile controls (joystick)
    if (this.mobileControls) {
      const movement = this.mobileControls.getMovementVector();
      if (movement.x !== 0) {
        velocityX = movement.x * PLAYER_CONFIG.speed;
      }
      this.mobileControls.update();
    }

    // Keyboard controls (if no mobile input) - support WASD
    if (velocityX === 0 && this.input.keyboard) {
      const keyboard = this.input.keyboard;
      const isADown = keyboard.addKey('A').isDown;
      const isDDown = keyboard.addKey('D').isDown;

      if (this.cursors?.left?.isDown || isADown) {
        velocityX = -PLAYER_CONFIG.speed;
      } else if (this.cursors?.right?.isDown || isDDown) {
        velocityX = PLAYER_CONFIG.speed;
      }
    }

    this.player.setVelocityX(velocityX);

    // Jump with edge physics boost
    if (this.input.keyboard) {
      const keyboard = this.input.keyboard;
      const isWDown = keyboard.addKey('W').isDown;
      if ((this.jumpKey?.isDown || this.cursors?.up?.isDown || isWDown) && this.player.isOnGround()) {
        this.player.jumpWithBoost(this.currentJumpBoost);
        this.currentJumpBoost = 1.0;
      }

      // Dash
      if (this.dashKey?.isDown) {
        this.player.dash(velocityX >= 0 ? 1 : -1);
      }
      
      // Attack
      if (this.attackKey?.isDown) {
        this.handlePlayerAttack();
      }
    }

    // Apply edge-based physics (only if scene is valid)
    if (this.isSceneActive() && this.player && this.player.active) {
      this.applyEdgePhysics();

      // Update player
      this.player.update(time, delta);
    }
    
    // Update boss (only if scene is valid)
    if (this.isSceneActive()) {
      this.updateBoss(time, delta);
    }
  }
  
  // Clean up when scene is destroyed
  shutdown() {
    // Mark as game over to prevent any further updates
    this.isGameOver = true;
    this.isPaused = true;
    
    // Kill all tweens first
    this.tweens.killAll();
    
    // Clean up boss
    if (this.boss) {
      this.boss.destroy();
      this.boss = undefined as any;
    }
    
    // Clear attack hint text
    if (this.attackHintText) {
      this.attackHintText.destroy();
      this.attackHintText = undefined;
    }
    
    // Clear special meter
    if (this.specialMeter) {
      this.specialMeter.destroy();
      this.specialMeter = undefined as any;
    }
    
    // Reset state
    this.specialValue = 0;
    
    // Call parent cleanup
    super.shutdown();
  }
}
